import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ClipLoader } from 'react-spinners';
import {
  KeyIcon,
  EnvelopeIcon,
  CheckIcon,
  CheckBadgeIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline';

import {
  loadPreferences,
  savePreferences,
} from '../../../redux/actions/preferences';
import { useAlert } from '../../../context/AlertContext';
import api from '../../../utils/axiosConfig';

/* ────────────────────── shared atoms (local) ─────────────────────────────── */

const Eyebrow = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.14em] text-muted font-bold mb-1.5">
    {children}
  </div>
);

const TabHeader = ({ eyebrow, title, sub, actions }) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-6 mb-6 pb-5 border-b border-line">
    <div className="min-w-0">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="text-[26px] sm:text-[30px] md:text-[34px] leading-[1.05] tracking-tight text-ink-strong font-bold break-words">
        {title}
      </h2>
      {sub && (
        <p className="text-[14px] text-muted mt-2 max-w-xl leading-relaxed">
          {sub}
        </p>
      )}
    </div>
    {actions && (
      <div className="flex items-center gap-2 flex-wrap">{actions}</div>
    )}
  </div>
);

const Card = ({ children, className = '', padded = true }) => (
  <section
    className={`bg-white border border-line rounded-2xl shadow-softer ${
      padded ? 'p-6' : ''
    } ${className}`}
  >
    {children}
  </section>
);

const CardHeader = ({ eyebrow, title, sub, action }) => (
  <header className="flex items-start justify-between gap-4 mb-5">
    <div className="min-w-0">
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h3 className="text-[17px] sm:text-[19px] font-bold tracking-tight text-ink-strong leading-snug">
        {title}
      </h3>
      {sub && <p className="text-sm text-muted mt-1 max-w-prose">{sub}</p>}
    </div>
    {action}
  </header>
);

const Toggle = ({ checked, onChange, label, sub, isLast }) => (
  <label
    className={`flex items-start justify-between gap-6 py-4 cursor-pointer ${
      isLast ? '' : 'border-b border-line/70'
    }`}
  >
    <div className="min-w-0">
      <div className="text-[14.5px] text-ink-strong font-medium">{label}</div>
      {sub && (
        <div className="text-[13px] text-muted mt-0.5 leading-relaxed max-w-md">
          {sub}
        </div>
      )}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative shrink-0 w-11 h-6 rounded-full transition-colors mt-0.5 ${
        checked ? 'bg-ink-strong' : 'bg-line'
      }`}
    >
      <span
        className={`absolute top-0.5 ${
          checked ? 'left-[22px]' : 'left-0.5'
        } w-5 h-5 rounded-full bg-white transition-all shadow`}
      />
    </button>
  </label>
);

/* ────────────────────────── SecurityPrivacy ──────────────────────────────── */

const DEFAULTS = {
  profileVisibility: 'public',
  showPortfolioToGuests: true,
  appearInEventSearch: true,
};

const SecurityPrivacy = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showAlert } = useAlert();

  const { preferences } = useSelector((s) => s.preferences || {});
  const authUser = useSelector((s) => s.auth?.user);
  const brand = useSelector((s) => s.brands?.brand);
  const promoter = useSelector((s) => s.promoter?.promoter);
  const role = authUser?.role || 'promoter';
  const isBrand = role === 'brand';
  const profile = isBrand ? brand : promoter;

  const email =
    profile?.email || authUser?.email || authUser?.username || null;
  const emailVerified = Boolean(profile?.emailConfirmed);

  // Email change: pendingEmail is set server-side until the link mailed to
  // the new address is clicked; the current email keeps working meanwhile.
  const [pendingEmail, setPendingEmail] = useState(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [emailError, setEmailError] = useState(null);

  useEffect(() => {
    setPendingEmail(profile?.pendingEmail || null);
  }, [profile?.pendingEmail]);

  // Password change (authenticated flow — distinct from the logged-out
  // "forgot password" reset that goes through /reset-password).
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState(null);

  const resetPasswordForm = useCallback(() => {
    setShowPasswordForm(false);
    setPasswordError(null);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  }, []);

  const handleChangePassword = useCallback(
    async (e) => {
      e.preventDefault();
      if (!currentPassword || !newPassword || passwordSubmitting) return;

      if (newPassword.length < 6) {
        setPasswordError(t('password_change.error_short'));
        return;
      }
      if (newPassword !== confirmPassword) {
        setPasswordError(t('password_change.error_mismatch'));
        return;
      }

      setPasswordSubmitting(true);
      setPasswordError(null);
      try {
        await api.post('/auth/change-password', {
          currentPassword,
          newPassword,
        });
        resetPasswordForm();
        showAlert({
          title: t('password_change.success_title'),
          message: t('password_change.success_message'),
          primaryAction: { label: t('common.ok') },
        });
      } catch (err) {
        const status = err?.response?.status;
        const serverMessage = err?.response?.data?.message || '';
        if (status === 400 && /invalid password/i.test(serverMessage)) {
          setPasswordError(t('password_change.error_current'));
        } else if (status === 400 && /6 characters/i.test(serverMessage)) {
          setPasswordError(t('password_change.error_short'));
        } else {
          setPasswordError(t('password_change.error_generic'));
        }
      } finally {
        setPasswordSubmitting(false);
      }
    },
    [
      currentPassword,
      newPassword,
      confirmPassword,
      passwordSubmitting,
      resetPasswordForm,
      showAlert,
      t,
    ]
  );

  const handleRequestEmailChange = useCallback(
    async (e) => {
      e.preventDefault();
      if (!newEmail.trim() || !emailPassword || emailSubmitting) return;

      setEmailSubmitting(true);
      setEmailError(null);
      try {
        const { data } = await api.post('/auth/request-email-change', {
          newEmail: newEmail.trim(),
          password: emailPassword,
        });
        setPendingEmail(data?.pendingEmail || newEmail.trim());
        setShowEmailForm(false);
        setNewEmail('');
        setEmailPassword('');
        showAlert({
          title: t('email_change.request_sent_title'),
          message: t('email_change.request_sent_message', {
            email: data?.pendingEmail || newEmail.trim(),
          }),
          primaryAction: { label: t('common.ok') },
        });
      } catch (err) {
        const status = err?.response?.status;
        const serverMessage = err?.response?.data?.message || '';
        if (status === 409) {
          setEmailError(t('email_change.error_in_use'));
        } else if (status === 400 && /password/i.test(serverMessage)) {
          setEmailError(t('email_change.error_password'));
        } else if (status === 400 && /same/i.test(serverMessage)) {
          setEmailError(t('email_change.error_same'));
        } else {
          setEmailError(t('email_change.error_generic'));
        }
      } finally {
        setEmailSubmitting(false);
      }
    },
    [newEmail, emailPassword, emailSubmitting, showAlert, t]
  );

  const [profileVisibility, setProfileVisibility] = useState(
    DEFAULTS.profileVisibility
  );
  const [showPortfolioToGuests, setShowPortfolioToGuests] = useState(
    DEFAULTS.showPortfolioToGuests
  );
  const [appearInEventSearch, setAppearInEventSearch] = useState(
    DEFAULTS.appearInEventSearch
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(loadPreferences());
  }, [dispatch]);

  useEffect(() => {
    if (!preferences) return;
    if (preferences.profileVisibility)
      setProfileVisibility(preferences.profileVisibility);
    if (typeof preferences.showPortfolioToGuests === 'boolean')
      setShowPortfolioToGuests(preferences.showPortfolioToGuests);
    if (typeof preferences.appearInEventSearch === 'boolean')
      setAppearInEventSearch(preferences.appearInEventSearch);
  }, [preferences]);

  const initial = useMemo(
    () => ({
      profileVisibility:
        preferences?.profileVisibility || DEFAULTS.profileVisibility,
      showPortfolioToGuests:
        typeof preferences?.showPortfolioToGuests === 'boolean'
          ? preferences.showPortfolioToGuests
          : DEFAULTS.showPortfolioToGuests,
      appearInEventSearch:
        typeof preferences?.appearInEventSearch === 'boolean'
          ? preferences.appearInEventSearch
          : DEFAULTS.appearInEventSearch,
    }),
    [preferences]
  );

  const isDirty =
    profileVisibility !== initial.profileVisibility ||
    showPortfolioToGuests !== initial.showPortfolioToGuests ||
    appearInEventSearch !== initial.appearInEventSearch;

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const payload = {
        profileVisibility,
        showPortfolioToGuests,
        appearInEventSearch,
      };
      await dispatch(savePreferences(payload));
      showAlert({
        title: t('dashboard.security_privacy_page.saved_title'),
        message: t('dashboard.security_privacy_page.saved_message'),
        primaryAction: { label: t('common.ok') },
      });
    } catch {
      showAlert({
        title: t('dashboard.security_privacy_page.save_error_title'),
        message: t('dashboard.security_privacy_page.save_error_message'),
        primaryAction: { label: t('common.ok') },
      });
    } finally {
      setSaving(false);
    }
  }, [
    dispatch,
    profileVisibility,
    showPortfolioToGuests,
    appearInEventSearch,
    showAlert,
    t,
  ]);

  const handleReset = useCallback(() => {
    setProfileVisibility(DEFAULTS.profileVisibility);
    setShowPortfolioToGuests(DEFAULTS.showPortfolioToGuests);
    setAppearInEventSearch(DEFAULTS.appearInEventSearch);
  }, []);

  return (
    <div className="space-y-6">
      <TabHeader
        eyebrow={t('dashboard.security_privacy_page.eyebrow')}
        title={t('dashboard.security_privacy_page.title')}
        sub={t('dashboard.security_privacy_page.subtitle')}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Password */}
        <Card>
          <CardHeader
            eyebrow={t('dashboard.security_privacy_page.password_eyebrow')}
            title={t('dashboard.security_privacy_page.password_title')}
            sub={t('dashboard.security_privacy_page.password_sub')}
          />
          {!showPasswordForm && (
            <button
              type="button"
              onClick={() => {
                setShowPasswordForm(true);
                setPasswordError(null);
              }}
              className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-full bg-white text-ink-strong border border-line hover:border-ink/30 text-sm font-bold transition-colors"
            >
              <KeyIcon className="w-4 h-4" />
              {t('dashboard.security_privacy_page.change_password')}
            </button>
          )}

          {showPasswordForm && (
            <form onSubmit={handleChangePassword} className="space-y-3">
              <input
                type="password"
                required
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder={t('password_change.current_placeholder')}
                className="w-full h-10 px-3 rounded-xl border border-line text-[14px] text-ink-strong focus:outline-none focus:border-ink/40"
              />
              <input
                type="password"
                required
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t('password_change.new_placeholder')}
                className="w-full h-10 px-3 rounded-xl border border-line text-[14px] text-ink-strong focus:outline-none focus:border-ink/40"
              />
              <input
                type="password"
                required
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('password_change.confirm_placeholder')}
                className="w-full h-10 px-3 rounded-xl border border-line text-[14px] text-ink-strong focus:outline-none focus:border-ink/40"
              />
              {passwordError && (
                <p className="text-[13px] text-rose-600">{passwordError}</p>
              )}
              <div className="flex items-center gap-2">
                <button
                  type="submit"
                  disabled={
                    passwordSubmitting ||
                    !currentPassword ||
                    !newPassword ||
                    !confirmPassword
                  }
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-brand hover:bg-brand-hover text-ink-strong text-[13px] font-bold shadow-softer disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {passwordSubmitting ? (
                    <ClipLoader color="#1A1A1F" size={13} />
                  ) : (
                    <CheckIcon className="w-4 h-4" />
                  )}
                  {t('password_change.submit')}
                </button>
                <button
                  type="button"
                  disabled={passwordSubmitting}
                  onClick={resetPasswordForm}
                  className="inline-flex items-center h-9 px-4 rounded-full text-ink hover:bg-paper-2 text-[13px] font-bold disabled:opacity-50"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          )}
        </Card>

        {/* Email */}
        <Card>
          <CardHeader
            eyebrow={t('dashboard.security_privacy_page.email_eyebrow')}
            title={t('dashboard.security_privacy_page.email_title')}
          />
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-brand-softer flex items-center justify-center shrink-0">
              <EnvelopeIcon className="w-4 h-4 text-yellow-03" />
            </div>
            <div className="min-w-0 flex-1">
              <div
                className="text-[14px] text-ink-strong font-medium truncate"
                title={email || ''}
              >
                {email || '—'}
              </div>
              {emailVerified && (
                <div className="text-[12.5px] text-emerald-700 mt-1 inline-flex items-center gap-1.5">
                  <CheckBadgeIcon className="w-3.5 h-3.5" />
                  {t('dashboard.verified')}
                </div>
              )}
            </div>
            {!showEmailForm && (
              <button
                type="button"
                onClick={() => {
                  setShowEmailForm(true);
                  setEmailError(null);
                }}
                className="inline-flex items-center justify-center gap-2 h-9 px-4 rounded-full bg-white text-ink-strong border border-line hover:border-ink/30 text-[13px] font-bold transition-colors shrink-0"
              >
                {t('email_change.change_button')}
              </button>
            )}
          </div>

          {pendingEmail && (
            <div className="mt-4 text-[13px] text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 leading-relaxed">
              {t('email_change.pending_notice', { email: pendingEmail })}
            </div>
          )}

          {showEmailForm && (
            <form onSubmit={handleRequestEmailChange} className="mt-5 space-y-3">
              <input
                type="email"
                required
                autoComplete="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder={t('email_change.new_email_placeholder')}
                className="w-full h-10 px-3 rounded-xl border border-line text-[14px] text-ink-strong focus:outline-none focus:border-ink/40"
              />
              <input
                type="password"
                required
                autoComplete="current-password"
                value={emailPassword}
                onChange={(e) => setEmailPassword(e.target.value)}
                placeholder={t('email_change.password_placeholder')}
                className="w-full h-10 px-3 rounded-xl border border-line text-[14px] text-ink-strong focus:outline-none focus:border-ink/40"
              />
              <p className="text-[12.5px] text-muted leading-relaxed">
                {t('email_change.form_hint')}
              </p>
              {emailError && (
                <p className="text-[13px] text-rose-600">{emailError}</p>
              )}
              <div className="flex items-center gap-2">
                <button
                  type="submit"
                  disabled={emailSubmitting || !newEmail.trim() || !emailPassword}
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-brand hover:bg-brand-hover text-ink-strong text-[13px] font-bold shadow-softer disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {emailSubmitting ? (
                    <ClipLoader color="#1A1A1F" size={13} />
                  ) : (
                    <CheckIcon className="w-4 h-4" />
                  )}
                  {t('email_change.submit')}
                </button>
                <button
                  type="button"
                  disabled={emailSubmitting}
                  onClick={() => {
                    setShowEmailForm(false);
                    setEmailError(null);
                    setNewEmail('');
                    setEmailPassword('');
                  }}
                  className="inline-flex items-center h-9 px-4 rounded-full text-ink hover:bg-paper-2 text-[13px] font-bold disabled:opacity-50"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          )}
        </Card>
      </div>

      {/* Privacy & Visibility */}
      <Card>
        <CardHeader
          eyebrow={t('dashboard.security_privacy_page.privacy_eyebrow')}
          title={t('dashboard.security_privacy_page.privacy_title')}
        />
        <Toggle
          label={t('dashboard.security_privacy_page.profile_public_label')}
          sub={t('dashboard.security_privacy_page.profile_public_sub')}
          checked={profileVisibility === 'public'}
          onChange={(on) => setProfileVisibility(on ? 'public' : 'private')}
        />
        <Toggle
          label={t('dashboard.security_privacy_page.show_portfolio_label')}
          sub={t('dashboard.security_privacy_page.show_portfolio_sub')}
          checked={showPortfolioToGuests}
          onChange={setShowPortfolioToGuests}
          isLast
        />
      </Card>

      {/* Discoverability */}
      <Card>
        <CardHeader
          eyebrow={t('dashboard.security_privacy_page.discoverability_eyebrow')}
          title={t('dashboard.security_privacy_page.discoverability_title')}
        />
        {isBrand ? (
          <Toggle
            label={t('dashboard.security_privacy_page.appear_event_label')}
            sub={t('dashboard.security_privacy_page.appear_event_sub')}
            checked={appearInEventSearch}
            onChange={setAppearInEventSearch}
            isLast
          />
        ) : (
          <Toggle
            label={t(
              'dashboard.security_privacy_page.appear_promoter_events_label'
            )}
            sub={t(
              'dashboard.security_privacy_page.appear_promoter_events_sub'
            )}
            checked={appearInEventSearch}
            onChange={setAppearInEventSearch}
            isLast
          />
        )}
      </Card>

      {/* Save / Reset */}
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={handleReset}
          disabled={saving}
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-ink hover:bg-paper-2 text-[13px] font-bold disabled:opacity-50"
        >
          <ArrowUturnLeftIcon className="w-4 h-4" />
          {t('common.reset')}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !isDirty}
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-brand hover:bg-brand-hover text-ink-strong text-[13px] font-bold shadow-softer disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? (
            <ClipLoader color="#1A1A1F" size={14} />
          ) : (
            <CheckIcon className="w-4 h-4" />
          )}
          {saving ? t('dashboard.saving') : t('common.save')}
        </button>
      </div>

      {/* Delete account — commented out until the backend flow exists.
          Re-import TrashIcon from @heroicons/react/24/outline when restoring.
      <Card className="border-rose-100 bg-rose-50/30">
        <CardHeader
          eyebrow="Zona de perigo"
          title="Eliminar conta"
          sub="Esta ação é permanente. Todos os seus dados, reservas e histórico serão removidos."
        />
        <button
          type="button"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-rose-50 text-rose-700 border border-rose-100 hover:bg-rose-100 text-sm font-bold"
        >
          <TrashIcon className="w-4 h-4" />
          Eliminar a minha conta
        </button>
      </Card>
      */}
    </div>
  );
};

export default SecurityPrivacy;
